import BaseCard from "./base-card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatToPKR } from "@/lib/helper";
import { Product, ProductType } from "@/types/system-setting/product";
import { POSProductType } from "@/types/enums"; // Added import
import { SellableItem } from "@/types/pos/transaction";
const { VITE_VIEW_S3_URL } = import.meta.env;

// Extended SellableItem for display purposes
type ExtendedSellableItemForDisplay = SellableItem & {
  name?: string;
  image_url?: string | null;
  net_price?: number;
  flexible_price?: boolean;
  manage_inventory?: boolean;
  type?: ProductType;
};

interface ProductCardProps {
  item: ExtendedSellableItemForDisplay;
  categoryType: POSProductType; // New prop for main category type
  disabled?: boolean;
  onSelect: () => void;
}
// same card is used for both product and memberships
const ProductCard = ({ item, categoryType, disabled, onSelect }: ProductCardProps) => {
  const isMembership = categoryType === POSProductType.Membership;

  const hasImage = Boolean(item.image_url) && !isMembership;
  const displayImage =
    item.image_url && item.image_url !== ""
      ? item.image_url.includes("https://")
        ? item.image_url
        : `${VITE_VIEW_S3_URL}/${item.image_url}`
      : "";

  const priceLabel = item.flexible_price?"Flexible Price": formatToPKR(item.net_price || 0);

  const showQuantity = item.manage_inventory && !isMembership;
  const quantityValue = Number(item.quantity) ?? 0;
  const quantityLabel = `Qty: ${quantityValue}`


  const lowStock = showQuantity && quantityValue <= 5;

  return (
    <BaseCard
      onClick={onSelect}
      disabled={disabled}
      ariaLabel={`Add ${item.name}`}
      className="flex-col" // Add flex-col to BaseCard for consistent layout
    >
      {!isMembership && (
        <div className="relative aspect-square h-24 overflow-hidden rounded-md bg-gradient-to-br from-slate-50 to-slate-100">
          {hasImage ? (
            <img
              src={displayImage}
              alt={item.name}
              className="h-24 object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-24 items-center justify-center bg-gradient-to-br from-primary/10 to-primary/30 text-2xl font-semibold text-primary">
              {item.name?.charAt(0)?.toUpperCase() ?? "P"}
            </div>
          )}
          <div className="absolute left-2 top-2 flex flex-col gap-1">
            {categoryType === POSProductType.TrainingPackage && (
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                1-on-1
              </Badge>
            )}
            {item.type === ProductType.Wallet && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                Wallet
              </Badge>
            )}
            {item.flexible_price && (
              <Badge variant="outline" className="border-blue-200 text-blue-700">
                Flexible
              </Badge>
            )}
            {lowStock && (
              <Badge variant="destructive" className="bg-red-100 text-red-700">
                Low stock
              </Badge>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1 p-2.5"> {/* Added padding for consistent spacing */}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="line-clamp-1 text-sm font-semibold capitalize">
                {item.name}
              </p>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="max-w-xs break-words text-sm capitalize"
            >
              {item.name}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <p className="text-base font-bold text-primary leading-none">{priceLabel}</p>
        {showQuantity && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span
              className={cn(
                "font-medium text-xs",
                lowStock ? "text-red-600" : "text-muted-foreground"
              )}
            >
              {quantityLabel}
            </span>
          </div>
        )}
      </div>
    </BaseCard>
  );
};

export default ProductCard;

